// Pieces the pricing screens share (Sprint 4; UC-PRICING-002..006, -009): the words of the rounding rules and of a list's kind, who
// set a price as `products.html` writes it ("R Naude · 0001"), a band's range and keys, the examples of Settings › Markup & fitting
// and the price a product markup gives — computed with the money package's own `markup` and `roundPrice` (TC-MONEY-006..014), the
// functions pricing's proposals use, so the screens hold no formula of their own — and the price history's table (UC-PRICING-003).
// Nothing here invents a class: the markup is the prototype's.
import {
  bp, cents, controlIdDisplay,
  type FrameMarkupBandRef, type FrameMarkupBandView, type PriceHistoryEntryView, type PriceHistorySource, type PricingUserRef, type RoundingRule, type SettingSource,
  type SupplierPriceListKind,
} from "@visionx/contracts";
import { applyPercent, markup, roundPrice } from "@visionx/money";
import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { settingStore } from "../api/endpoints";
import { formatCount, formatDate, formatMoney, formatTime, isLocalDate, localDate, percentFromBp } from "../lib/format";
import { ImportSource } from "./ImportsShared";

/** A list item's version as the `If-Match` the write needs (the routes' `W/"<version>"`). */
export const etagOf = (r: { version: number }): string => `W/"${r.version}"`;

/** Today in the organisation's timezone, `YYYY-MM-DD` — the first day a branch price or a list's end may take. */
export const todayLocal = (): string => localDate(new Date());

/** A calendar day `YYYY-MM-DD` as the screens write it, `14 Aug 2026` (midday, so no timezone moves the day). */
export const dayText = (day: string | null | undefined): string => (day && isLocalDate(day) ? formatDate(`${day}T12:00:00.000Z`) : "—");

/** `Ruahan Naude` → `R Naude`, as `products.html` names who set a price. */
export function shortName(displayName: string): string {
  const words = displayName.trim().split(/\s+/).filter(Boolean);
  if (words.length < 2) return displayName.trim();
  return `${words[0]!.charAt(0)} ${words[words.length - 1]}`;
}

/** Who set or changed something: `R Naude · 0001`. */
export function byLine(u: PricingUserRef | null | undefined): ReactNode {
  if (!u) return <span className="faint">—</span>;
  return <>{shortName(u.displayName)}{u.controlId !== null && <> · <span className="mono">{controlIdDisplay(u.controlId)}</span></>}</>;
}

/** `Menlyn's`, `Silver Lakes'`. */
export const possessive = (name: string): string => (/s$/i.test(name) ? `${name}'` : `${name}'s`);

/** `one`, `two` … `nine`, then the figure. */
export const countWord = (n: number): string => ["no", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine"][n] ?? formatCount(n);

// --- the rounding rules (BR-PRICING-003) -----------------------------------------------------------------------------------

/** The rules as a setting's value reads (UC-PRICING-006): `Nearest R 1.00`, `Up to nearest R 10.00`. */
export const ROUNDING_LABEL: Readonly<Record<RoundingRule, string>> = {
  None: "Not rounded", NearestR1: "Nearest R 1.00", UpToNearestR5: "Up to nearest R 5.00", UpToNearestR10: "Up to nearest R 10.00", NearestR10: "Nearest R 10.00",
  ToX99: "Up to a price ending in 99 cents", ToX95: "Up to a price ending in 95 cents",
};

/** The rules inside a sentence: "priced at the lab price + 90%, rounded to the nearest R 10.00". */
export const ROUNDING_WORDS: Readonly<Record<RoundingRule, string>> = {
  None: "not rounded", NearestR1: "rounded to the nearest R 1.00", UpToNearestR5: "rounded up to the nearest R 5.00", UpToNearestR10: "rounded up to the nearest R 10.00",
  NearestR10: "rounded to the nearest R 10.00", ToX99: "rounded up to a price ending in 99 cents", ToX95: "rounded up to a price ending in 95 cents",
};

/** Where a setting's value comes from, in words: the default, the organisation's, or the branch's own. */
export function sourceWord(source: SettingSource, branchName: string | null): string {
  if (source === "branch") return branchName ? `${possessive(branchName)} own` : "the branch's own";
  return source === "tenant" ? "organisation" : "default";
}

/** A supplier price list's kind in words (BR-PRICING-019). */
export const LIST_KIND_WORDS: Readonly<Record<SupplierPriceListKind, string>> = { Lens: "lenses", Coating: "coatings", Colour: "tints", PPF: "PPF", Mixed: "lenses and coatings" };

/** The `pricing` settings at a scope — `org` or a branch — each with where it comes from (UC-TENANCY-007; every role reads them). */
export function usePricingSettings(scope: string | null) {
  return useQuery({ queryKey: ["settings", scope ?? "", "pricing"], queryFn: () => settingStore.read(scope!, "pricing"), enabled: scope !== null, staleTime: 30_000 });
}

/** A rounding setting's value, the rule the API answered or `NearestR1`, the default (BR-PRICING-018). */
export function ruleOf(settings: Record<string, { value: unknown; source: SettingSource }> | undefined, key: "roundingRule" | "lensRoundingRule"): RoundingRule {
  const v = settings?.[key]?.value;
  return typeof v === "string" && v in ROUNDING_LABEL ? (v as RoundingRule) : "NearestR1";
}

// --- bands (BR-PRICING-004) ------------------------------------------------------------------------------------------------

/** A markup as `products.html` writes it: `400%`, `60% + R 5.00`. */
export const markupText = (percentBp: number, valueCents: number): string => `${percentFromBp(percentBp)}${valueCents > 0 ? ` + ${formatMoney(valueCents)}` : ""}`;

/** A band's range — from inclusive to exclusive (BR-PRICING-004): `R 0.00–R 500.00`, `R 3 000.00 and above`. */
export const rangeText = (b: Pick<FrameMarkupBandRef, "rangeStartCents" | "rangeEndCents">): string =>
  (b.rangeEndCents === null ? `${formatMoney(b.rangeStartCents)} and above` : `${formatMoney(b.rangeStartCents)}–${formatMoney(b.rangeEndCents)}`);

/** Whether a band has no key: it prices every frame of its scope. */
export const isPlain = (b: Pick<FrameMarkupBandRef, "productId" | "supplierId" | "brand">): boolean => !b.productId && !b.supplierId && !(b.brand && b.brand.trim());

/**
 * Whom a band is for, in words: its product, supplier and brand — `Lindberg SA · Lindberg` — or `all suppliers` without a key. A
 * band known only by its reference (an overlap's) is named from the bands read, when it is one of them.
 */
export function bandKeyWords(b: FrameMarkupBandRef, known?: readonly FrameMarkupBandView[]): string {
  if (isPlain(b)) return "all suppliers";
  const v = known?.find((x) => x.id === b.id) ?? ("supplier" in b ? (b as FrameMarkupBandView) : undefined);
  const keys = [
    b.productId ? v?.product?.sku ?? "one product" : null,
    b.supplierId ? v?.supplier?.name || "one supplier" : null,
    b.brand && b.brand.trim() ? b.brand.trim() : null,
  ].filter((k): k is string => !!k);
  return keys.join(" · ");
}

/**
 * A band's example (UC-PRICING-004 open questions, Proposed): a closed band at its midpoint, the open band at its from-value, through
 * the band at the rounding rule given — `R 250.00 → R 1 250.00`. The money package computes both figures.
 */
export function bandExample(b: Pick<FrameMarkupBandRef, "rangeStartCents" | "rangeEndCents" | "marginPercentBp" | "marginValueCents">, rule: RoundingRule): { cost: number; price: number } {
  const start = cents(b.rangeStartCents);
  const cost = b.rangeEndCents === null ? start : cents(start + applyPercent(cents(b.rangeEndCents - b.rangeStartCents), bp(5_000)));
  return { cost, price: roundPrice(markup(cost, bp(b.marginPercentBp), cents(b.marginValueCents)), rule) };
}

/** A band as a sentence names it: `R 0.00–R 500.00 at 400%`. */
export const bandText = (b: Pick<FrameMarkupBandRef, "rangeStartCents" | "rangeEndCents" | "marginPercentBp" | "marginValueCents">): string =>
  `${rangeText(b)} at ${markupText(b.marginPercentBp, b.marginValueCents)}`;

/**
 * UC-PRICING-004 E1: the bands an overlap names (`meta.overlaps`, every active band of the scope and key it crosses), in words —
 * "Overlaps R 0.00–R 500.00 at 400% (all suppliers)"; they share one scope and key, so the key is said once.
 */
export function overlapWords(overlaps: unknown, known: readonly FrameMarkupBandView[]): string | null {
  if (!Array.isArray(overlaps) || overlaps.length === 0) return null;
  const refs = overlaps.filter((o): o is FrameMarkupBandRef => typeof o === "object" && o !== null && typeof (o as FrameMarkupBandRef).rangeStartCents === "number");
  if (refs.length === 0) return null;
  const texts = refs.map(bandText);
  const list = texts.length === 1 ? texts[0]! : `${texts.slice(0, -1).join(", ")} and ${texts[texts.length - 1]}`;
  return `Overlaps ${list} (${bandKeyWords(refs[0]!, known)})`;
}

/** UC-PRICING-004 E5: what someone else changed on a band, as it is now — "290%", or "R 500.00–R 1 200.00 at 290%". */
export function bandChangeWords(read: FrameMarkupBandView, now: FrameMarkupBandView): string {
  const range = read.rangeStartCents !== now.rangeStartCents || read.rangeEndCents !== now.rangeEndCents;
  const pct = read.marginPercentBp !== now.marginPercentBp || read.marginValueCents !== now.marginValueCents;
  const key = read.productId !== now.productId || read.supplierId !== now.supplierId || (read.brand ?? "") !== (now.brand ?? "");
  const parts = [range && pct ? bandText(now) : range ? rangeText(now) : pct ? markupText(now.marginPercentBp, now.marginValueCents) : null, key ? `for ${bandKeyWords(now)}` : null].filter(Boolean);
  return parts.length > 0 ? parts.join(" ") : bandText(now);
}

/** What a markup gives for a cost at a rule (BR-PRICING-002, -003): the money package's `markup`, then `roundPrice`. */
export const markupPrice = (costCents: number, percentBp: number, valueCents: number, rule: RoundingRule): number =>
  roundPrice(markup(cents(costCents), bp(percentBp), cents(valueCents)), rule);

/** `cost × 5` for a band of 400% with no value: the multiplier the prototype's help names, or null where it says nothing simple. */
export function costTimes(b: Pick<FrameMarkupBandRef, "marginPercentBp" | "marginValueCents"> | undefined): string | null {
  if (!b || b.marginValueCents > 0) return null;
  return new Intl.NumberFormat("en-ZA", { maximumFractionDigits: 2 }).format((b.marginPercentBp + 10_000) / 10_000).replace(",", ".");
}

// --- the price history (UC-PRICING-003; BR-PRICING-013) -------------------------------------------------------------------

export const HISTORY_SOURCE_LABEL: Readonly<Record<PriceHistorySource, string>> = { Manual: "By hand", Receipt: "Receipt", OpeningStock: "Opening stock", Import: "Import" };

const BAND_FIELD: Readonly<Record<string, string>> = {
  rangeStartCents: "Cost from", rangeEndCents: "Cost to", marginPercentBp: "Markup", marginValueCents: "Value", productId: "Product", supplierId: "Supplier", brand: "Brand",
};
const LIST_FIELD: Readonly<Record<string, string>> = { lensMarkupBp: "Lens markup", discountBp: "Discount", effectiveTo: "Ends" };

/** The lens code a lab cost belongs to, for the product's history: its supplier and its code there. */
export interface HistoryProduct { supplierName?: string | null | undefined; supplierCode?: string | null | undefined }

/**
 * What an entry changed, in words. On a product's history (`product`) a band or a list is named as such ("Band markup"); on the
 * band's or the list's own history its fields are enough ("Markup", "Cost from").
 */
export function whatOf(e: PriceHistoryEntryView, product?: HistoryProduct): string {
  const at = e.branch ? ` · ${e.branch.name}` : "";
  switch (e.entity) {
    case "Product": return e.field === "priceCents" ? "Tenant price" : e.field === "supplierPriceCents" ? "Supplier price" : e.field;
    case "BranchPrice": return `Branch price${at}`;
    case "Serial": return "A unit's own price";
    case "ProductMarkup": return `${e.field === "valueCents" ? "Markup value" : "Markup"} · ${e.branch ? e.branch.name : "organisation"}`;
    case "FrameMarkupBand": {
      const f = BAND_FIELD[e.field] ?? e.field;
      return product ? `Band ${f.toLowerCase()}${at}` : f;
    }
    case "SupplierPriceList": return LIST_FIELD[e.field] ?? e.field;
    case "SupplierLensCode": {
      const code = [product?.supplierName, product?.supplierCode].filter(Boolean).join(" ");
      return `Lab cost${code ? ` · ${code}` : ""}${at}`;
    }
    case "LensComponentOption": return e.field === "costCents" ? "Option cost" : "Option price";
  }
}

/** An entry's old or new value in its kind: money, a percentage, a count, a date or a text. */
export function valueText(e: PriceHistoryEntryView, which: "old" | "new"): string {
  const n = which === "old" ? e.oldValue : e.newValue;
  const t = which === "old" ? e.oldText : e.newText;
  switch (e.valueKind) {
    case "Money": return n === null ? "—" : formatMoney(n);
    case "Percent": return n === null ? "—" : percentFromBp(n);
    case "Number": return n === null ? "—" : formatCount(n);
    case "Text":
      if (t === null) return "—";
      if (isLocalDate(t)) return dayText(t);
      return e.field === "productId" || e.field === "supplierId" ? "set" : t;
  }
}

/** Where a change came from, with the receipt's number, the proposal beside a kept price, a Control PIN and the reason. */
export function sourceCell(e: PriceHistoryEntryView): ReactNode {
  const head = e.source === "Receipt" && e.reference?.number ? `Receipt ${e.reference.number}` : HISTORY_SOURCE_LABEL[e.source];
  const notes = [
    e.proposalCents !== null ? `proposed ${formatMoney(e.proposalCents)}` : null,
    e.controlLogId ? "Control PIN" : null,
    e.reason ? `“${e.reason}”` : null,
  ].filter((x): x is string => x !== null);
  return <>{head}{e.source === "Import" && e.importJobId && <ImportSource importJobId={e.importJobId} />}{notes.length > 0 && <div className="small faint">{notes.join(" · ")}</div>}</>;
}

/** The history's rows, newest first: when, what, old, new, from, source and who (UC-PRICING-003 step 2). */
export function HistoryTable({ entries, product }: { entries: readonly PriceHistoryEntryView[]; product?: HistoryProduct | undefined }) {
  return (
    <table>
      <thead><tr><th>When</th><th>What</th><th className="r">Old</th><th className="r">New</th><th>From</th><th>Source</th><th>By</th></tr></thead>
      <tbody>
        {entries.map((e) => (
          <tr key={e.id}>
            <td className="nw">{formatDate(e.changedAt)}<div className="small faint">{formatTime(e.changedAt)}</div></td>
            <td>{whatOf(e, product)}</td>
            <td className="money">{valueText(e, "old")}</td>
            <td className="money strong">{valueText(e, "new")}</td>
            <td className="nw">{dayText(e.effectiveFrom)}</td>
            <td>{sourceCell(e)}</td>
            <td className="nw">{byLine(e.changedBy)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
